import React, { useState, useEffect, useCallback, useRef } from 'react';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Sidebar from '../../components/sidebar/Sidebar';
import musicGenerationService, {
  MusicProject,
  MusicStyle,
  MusicModel,
} from '../../services/api/musicGenerationService';
import {
  GenerationInputMode,
  GenerationInputModeTabs,
  ProductGenerationPanel,
  ProductSelectionModal,
  ProductSelectionData,
  ProductImageSelectionModal,
  SelectedProductImage,
} from '../../components/image-generation';
import { buildProductGenerationPrompt } from '../../utils/productGenerationPrompt';
import { selectOrganization } from '../../redux/organization/organizationSlice';
import { ResponsiveAppShell, TaskyTopBar, useConfirm, TaskyPageContainer } from '../../components/tasky';
import { useSetAdmanPageContext } from '../../components/adman/useAdmanPageContext';
import { useAdmanRefreshHandler } from '../../components/adman/useAdmanRefreshHandler';
import {
  Music, Plus, Trash2, X, Loader2, Play, Pause,
  Download, Volume2, VolumeX, Settings, Mic, MicOff,
  FileAudio, Clock, Image as ImageIcon,
} from 'lucide-react';

const MusicCreationPage: React.FC = () => {
  const confirm = useConfirm();
  const organization = useSelector(selectOrganization);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Data
  const [projects, setProjects] = useState<MusicProject[]>([]);
  const [styles, setStyles] = useState<MusicStyle[]>([]);
  const [models, setModels] = useState<Record<string, MusicModel>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [inputMode, setInputMode] = useState<GenerationInputMode>('product');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [title, setTitle] = useState('');
  const [selectedStyleId, setSelectedStyleId] = useState<number | undefined>();
  const [selectedModel, setSelectedModel] = useState('lyria-3-clip-preview');
  const [isGenerating, setIsGenerating] = useState(false);

  // Product selection
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);

  // Reference images for jacket
  const [referenceImages, setReferenceImages] = useState<SelectedProductImage[]>([]);
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);

  // Style selection modal
  const [showStyleModal, setShowStyleModal] = useState(false);

  // Vocals / Lyrics
  const [hasVocals, setHasVocals] = useState(false);
  const [vocalGender, setVocalGender] = useState<'male' | 'female'>('female');
  const [lyricsMode, setLyricsMode] = useState<'auto' | 'manual'>('auto');
  const [lyricsText, setLyricsText] = useState('');

  // Audio player
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Detail modal
  const [selectedProject, setSelectedProject] = useState<MusicProject | null>(null);

  // Polling
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- Data Loading ---
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [projectsRes, stylesRes, modelsRes] = await Promise.all([
        musicGenerationService.getProjects(),
        musicGenerationService.getStyles(),
        musicGenerationService.getModels(),
      ]);
      if (projectsRes.success) setProjects(projectsRes.data);
      if (stylesRes.success) setStyles(stylesRes.data);
      if (modelsRes.success) setModels(modelsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [fetchData]);

  // 広告自動化マンの「更新する」ボタンから呼べるようにする（F23-c）。
  useAdmanRefreshHandler(fetchData);

  // Deep-link: auto-select project when ?project=<uuid> query param is present.
  // Waits until projects list is loaded, then finds & sets the target as selected.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProcessedRef = useRef(false);
  useEffect(() => {
    if (deepLinkProcessedRef.current) return;
    const projectId = searchParams.get('project');
    if (!projectId) return;
    if (projects.length === 0) return; // wait for projects to load
    deepLinkProcessedRef.current = true;
    const target = projects.find((p) => p.id === projectId);
    if (target) {
      setSelectedProject(target);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('project');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, projects]);

  // Polling for generating projects
  useEffect(() => {
    const generatingProjects = projects.filter(
      (p) => p.status === 'pending' || p.status === 'generating_audio' || p.status === 'generating_jacket'
    );
    if (generatingProjects.length > 0) {
      pollingRef.current = setInterval(async () => {
        try {
          const res = await musicGenerationService.getProjects();
          if (res.success) setProjects(res.data);
        } catch { /* ignore */ }
      }, 5000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [projects]);

  // --- Product Selection ---
  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    if (data.selectedImages.length > 0) {
      setReferenceImages(data.selectedImages.map((image) => ({
        id: image.id,
        url: image.url,
        s3Key: image.file_path,
        productId: image.product_id,
        productName: data.product.name,
        fileName: image.file_name,
        category: image.category,
      })));
    }
    setShowProductSelectionModal(false);
  };

  // --- Generate ---
  const handleGenerate = async () => {
    if (inputMode === 'product' && !selectedProductData) {
      setError('商品を選択してください');
      return;
    }
    if (inputMode === 'freetext' && !prompt.trim()) {
      setError('プロンプトを入力してください');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);

      const effectiveProductInfo = selectedProductData
        ? buildProductGenerationPrompt(selectedProductData, additionalInstructions)
        : '';
      const effectivePrompt = inputMode === 'product'
        ? `次の商品・サービスの広告やブランド表現に合う音楽を生成してください。\n${effectiveProductInfo}`
        : prompt.trim();

      const res = await musicGenerationService.createProject({
        prompt: effectivePrompt,
        title: title.trim() || undefined,
        music_style_id: selectedStyleId,
        model: selectedModel,
        product_info: inputMode === 'product' ? effectiveProductInfo : undefined,
        has_vocals: hasVocals,
        vocal_gender: hasVocals ? vocalGender : undefined,
        lyrics_mode: hasVocals ? lyricsMode : undefined,
        lyrics_text: hasVocals && lyricsMode === 'manual' ? lyricsText : undefined,
        reference_image_s3_keys: referenceImages.length > 0 ? referenceImages.map(img => img.s3Key) : undefined,
        reference_image_categories: referenceImages.length > 0 ? referenceImages.map(img => img.category) : undefined,
      });

      if (res.success) {
        // Add the new project immediately to the top of the list
        setProjects((prev) => [res.data, ...prev]);
        setPrompt('');
        setTitle('');
        setSelectedStyleId(undefined);
        setSelectedProductData(null);
        setAdditionalInstructions('');
        setReferenceImages([]);
        setHasVocals(false);
        setLyricsMode('auto');
        setLyricsText('');
        setShowForm(false);
      } else {
        setError(res.error || '音楽の生成に失敗しました');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '音楽の生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  };

  // --- Audio ---
  const handlePlay = (project: MusicProject) => {
    if (!project.audio_url) return;
    if (playingId === project.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(project.audio_url);
    audio.muted = isMuted;
    audio.onended = () => setPlayingId(null);
    audio.play();
    audioRef.current = audio;
    setPlayingId(project.id);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) audioRef.current.muted = !isMuted;
  };

  const handleDownload = async (project: MusicProject) => {
    if (!project.audio_url) return;
    try {
      const response = await fetch(project.audio_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.title || 'music'}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'music', project_id: project.id });
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (projectId: string) => {
    if (!(await confirm({ title: 'この音楽を削除しますか？', tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      await musicGenerationService.deleteProject(projectId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      if (selectedProject?.id === projectId) setSelectedProject(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // --- Helpers ---
  const getStatusBadge = (status: MusicProject['status']) => {
    const configs: Record<string, { bg: string; text: string; label: string; spin?: boolean }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '待機中', spin: true },
      generating_audio: { bg: 'bg-blue-100', text: 'text-blue-700', label: '音楽生成中', spin: true },
      generating_jacket: { bg: 'bg-purple/10', text: 'text-purple', label: 'ジャケット生成中', spin: true },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: '完了' },
      failed: { bg: 'bg-red-100', text: 'text-red-700', label: '失敗' },
    };
    const c = configs[status];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${c.bg} ${c.text}`}>
        {c.spin && <Loader2 className="w-3 h-3 animate-spin" />}
        {c.label}
      </span>
    );
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const selectedStyleObj = styles.find(s => s.id === selectedStyleId);

  if (isLoading) {
    return (
      <ResponsiveAppShell
        sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
        mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
        topbarTitle="音楽生成"
      >
        <div className="flex min-h-[calc(100dvh-56px)] items-center justify-center bg-white lg:h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </ResponsiveAppShell>
    );
  }

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle="音楽生成"
      topbarActions={(
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-purple hover:bg-purple/10"
          aria-label="新規作成"
          type="button"
        >
          <Plus className="w-4 h-4" />
        </button>
      )}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="音楽生成"
          icon={<Music className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        >
          <button
            data-tutorial="ads-music-new"
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple text-white rounded-tasky-sm hover:bg-purple/90 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            新規作成
          </button>
        </TaskyTopBar>

        {/* Main Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* ========== Creation Form (LP-style) ========== */}
            {showForm && (
              <div className="mb-6 space-y-5 rounded-lg border border-warm-border bg-white p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-gray-900">音楽を生成</h2>
                  <button onClick={() => { setShowForm(false); setError(null); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <GenerationInputModeTabs value={inputMode} onChange={setInputMode} disabled={isGenerating} />

                {inputMode === 'product' && (
                  <ProductGenerationPanel
                    selectedProductData={selectedProductData}
                    additionalInstructions={additionalInstructions}
                    onAdditionalInstructionsChange={setAdditionalInstructions}
                    onSelectProduct={() => setShowProductSelectionModal(true)}
                    disabled={isGenerating}
                  />
                )}

                {/* Reference Images for Jacket */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">商品画像（ジャケットに反映・任意）</label>
                  <button
                    type="button"
                    onClick={() => setShowImageSelectionModal(true)}
                    className="text-sm text-purple hover:text-purple/80 underline flex items-center gap-1"
                  >
                    <ImageIcon className="w-3.5 h-3.5" />
                    商品画像を選択する
                  </button>
                  {referenceImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {referenceImages.map((img) => (
                        <div key={img.id} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-warm-border">
                          <img src={img.url} alt={img.fileName} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setReferenceImages(prev => prev.filter(i => i.id !== img.id))}
                            className="absolute top-0 right-0 p-0.5 bg-black/50 text-white rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">タイトル（任意）</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="例: 朝の爽やかBGM"
                    className="w-full px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Music Style Selection (Modal button + inline display) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">音楽スタイル（任意）</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowStyleModal(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors text-sm"
                    >
                      <Settings className="w-4 h-4" />
                      スタイルを選ぶ
                    </button>
                    {selectedStyleObj && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-purple/10 border border-purple/20 rounded-md">
                        <span className="text-sm text-purple font-medium">{selectedStyleObj.name}</span>
                        {selectedStyleObj.genre && (
                          <span className="text-xs bg-purple/10 text-purple px-1.5 py-0.5 rounded">{selectedStyleObj.genre}</span>
                        )}
                        <button type="button" onClick={() => setSelectedStyleId(undefined)} className="text-purple hover:text-purple/80">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Model Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">モデル</label>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    {Object.entries(models).map(([key, model]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedModel(key)}
                        className={`flex-1 p-3 border rounded-lg text-left text-sm transition-all ${
                          selectedModel === key
                            ? 'border-purple bg-purple/10 text-purple'
                            : 'border-warm-border hover:border-warm-border'
                        }`}
                      >
                        <div className="font-medium">{model.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{model.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Vocals Toggle */}
                <div className="border border-warm-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      {hasVocals ? <Mic className="w-4 h-4 text-purple" /> : <MicOff className="w-4 h-4 text-gray-400" />}
                      歌詞（ボーカル）
                    </label>
                    <button
                      type="button"
                      onClick={() => setHasVocals(!hasVocals)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        hasVocals ? 'bg-purple' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        hasVocals ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  {hasVocals && (
                    <div className="space-y-3 pt-2 border-t border-gray-100">
                      {/* Vocal Gender */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">ボーカル性別</label>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setVocalGender('female')}
                            className={`flex-1 px-3 py-2 border rounded-md text-sm transition-all ${
                              vocalGender === 'female'
                                ? 'border-purple bg-purple/10 text-purple font-medium'
                                : 'border-warm-border hover:border-warm-border text-gray-600'
                            }`}
                          >
                            女性ボーカル
                          </button>
                          <button
                            type="button"
                            onClick={() => setVocalGender('male')}
                            className={`flex-1 px-3 py-2 border rounded-md text-sm transition-all ${
                              vocalGender === 'male'
                                ? 'border-purple bg-purple/10 text-purple font-medium'
                                : 'border-warm-border hover:border-warm-border text-gray-600'
                            }`}
                          >
                            男性ボーカル
                          </button>
                        </div>
                      </div>

                      {/* Lyrics Mode */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">歌詞モード</label>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setLyricsMode('auto')}
                            className={`flex-1 px-3 py-2 border rounded-md text-sm transition-all ${
                              lyricsMode === 'auto'
                                ? 'border-purple bg-purple/10 text-purple font-medium'
                                : 'border-warm-border hover:border-warm-border text-gray-600'
                            }`}
                          >
                            自動生成
                          </button>
                          <button
                            type="button"
                            onClick={() => setLyricsMode('manual')}
                            className={`flex-1 px-3 py-2 border rounded-md text-sm transition-all ${
                              lyricsMode === 'manual'
                                ? 'border-purple bg-purple/10 text-purple font-medium'
                                : 'border-warm-border hover:border-warm-border text-gray-600'
                            }`}
                          >
                            手動入力
                          </button>
                        </div>
                      </div>

                      {/* Manual Lyrics Input */}
                      {lyricsMode === 'manual' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">歌詞テキスト</label>
                          <textarea
                            value={lyricsText}
                            onChange={(e) => setLyricsText(e.target.value)}
                            placeholder="歌詞を入力してください..."
                            className="w-full h-24 px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {inputMode === 'freetext' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      プロンプト <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="例: A bright acoustic pop with guitar and piano. Feel-good morning vibes for a coffee brand commercial."
                      rows={3}
                      className="w-full px-3 py-2 border border-warm-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">英語で記述すると精度が高くなります</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    (inputMode === 'product' ? !selectedProductData : !prompt.trim())
                  }
                  className="w-full sm:w-auto px-6 py-2.5 bg-purple text-white text-sm font-medium rounded-tasky-sm hover:bg-purple/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> 生成中...</>
                  ) : (
                    <><Music className="w-4 h-4" /> 生成する</>
                  )}
                </button>
              </div>
            )}

            {/* ========== Music Grid (Banner-style) ========== */}
            <div data-tutorial="ads-music-list">
            {projects.length === 0 && !showForm ? (
              <div className="text-center py-20">
                <Music className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-sm">まだ音楽がありません</p>
              </div>
            ) : projects.length > 0 && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="group bg-white border border-warm-border rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedProject(project)}
                  >
                    {/* Jacket Image */}
                    <div className="relative aspect-square bg-gradient-to-br from-purple/20 to-primary/20">
                      {project.jacket_url ? (
                        <img src={project.jacket_url} alt={project.title || 'Music jacket'} className="w-full h-full object-cover" loading="lazy" />
                      ) : project.status !== 'completed' && project.status !== 'failed' ? (
                        <div className="flex flex-col items-center justify-center h-full px-3">
                          <div className="relative mb-3">
                            <div className="w-14 h-14 rounded-full border-4 border-purple/20 border-t-purple animate-spin" />
                            <Music className="w-6 h-6 text-purple absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                          </div>
                          <span className="text-xs font-medium text-purple">
                            {project.status === 'pending' && '待機中...'}
                            {project.status === 'generating_audio' && '音楽を生成中...'}
                            {project.status === 'generating_jacket' && 'ジャケット生成中...'}
                          </span>
                          <div className="w-full mt-2 bg-purple/10 rounded-full h-1.5">
                            <div
                              className="bg-purple h-1.5 rounded-full transition-all duration-500"
                              style={{
                                width: project.status === 'pending' ? '10%'
                                  : project.status === 'generating_audio' ? '50%'
                                  : '85%',
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <Music className="w-12 h-12 text-purple/40" />
                        </div>
                      )}

                      {/* Play overlay */}
                      {project.status === 'completed' && project.audio_url && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePlay(project); }}
                          className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          {playingId === project.id ? (
                            <Pause className="w-12 h-12 text-white drop-shadow-lg" />
                          ) : (
                            <Play className="w-12 h-12 text-white drop-shadow-lg" />
                          )}
                        </button>
                      )}

                      {/* Failed overlay */}
                      {project.status === 'failed' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-red-900/20">
                          {getStatusBadge(project.status)}
                        </div>
                      )}

                      {/* Playing indicator */}
                      {playingId === project.id && (
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 bg-purple text-white rounded-full text-[10px]">
                          <Volume2 className="w-3 h-3" /> 再生中
                        </div>
                      )}

                      {/* Vocals badge */}
                      {project.has_vocals && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-white/90 text-purple rounded text-[10px] font-medium">
                          <Mic className="w-2.5 h-2.5" />
                          {project.vocal_gender === 'male' ? '男性' : '女性'}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{project.title || '無題'}</h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                        <span>{models[project.model]?.name || project.model}</span>
                        {project.file_size && (
                          <><span>·</span><span>{formatFileSize(project.file_size)}</span></>
                        )}
                      </div>
                      {project.music_style && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 bg-purple/5 rounded text-[10px] text-purple">
                          {project.music_style.name}
                        </span>
                      )}
                      <div className="text-[10px] text-gray-400 mt-1">{formatDate(project.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
          </TaskyPageContainer>
        </div>
      </div>

      {/* Product Selection Modal */}
      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      {/* Product Image Selection Modal for Jacket */}
      {showImageSelectionModal && (
        <ProductImageSelectionModal
          isOpen={showImageSelectionModal}
          onClose={() => setShowImageSelectionModal(false)}
          onSelect={(selected) => {
            setReferenceImages(selected);
            setShowImageSelectionModal(false);
          }}
          maxSelection={3}
        />
      )}

      {/* Music Style Selection Modal */}
      {showStyleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3" onClick={() => setShowStyleModal(false)}>
          <div className="max-h-[calc(100dvh-24px)] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-warm-border px-4 py-3 sm:px-6 sm:py-4">
              <h3 className="text-lg font-semibold text-gray-900">音楽スタイルを選択</h3>
              <button onClick={() => setShowStyleModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="max-h-[calc(100dvh-96px)] overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <button
                  onClick={() => { setSelectedStyleId(undefined); setShowStyleModal(false); }}
                  className={`p-4 border rounded-lg text-left transition-all ${
                    !selectedStyleId ? 'border-purple bg-purple/5' : 'border-warm-border hover:border-warm-border'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-900">指定なし</div>
                  <div className="text-xs text-gray-500 mt-1">自由入力のみ</div>
                </button>
                {styles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => { setSelectedStyleId(style.id); setShowStyleModal(false); }}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      selectedStyleId === style.id ? 'border-purple bg-purple/5' : 'border-warm-border hover:border-warm-border'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{style.name}</div>
                    {style.genre && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 bg-gray-100 rounded text-[10px] text-gray-500">{style.genre}</span>
                    )}
                    {style.description && (
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">{style.description}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3" onClick={() => setSelectedProject(null)}>
          <div className="max-h-[calc(100dvh-24px)] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Jacket */}
            <div className="relative aspect-square bg-gradient-to-br from-purple/20 to-primary/20">
              {selectedProject.jacket_url ? (
                <img src={selectedProject.jacket_url} alt={selectedProject.title || 'Music jacket'} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <Music className="w-20 h-20 text-purple/40" />
                </div>
              )}
              <button onClick={() => setSelectedProject(null)} className="absolute top-3 right-3 p-2 bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
              {selectedProject.status === 'completed' && selectedProject.audio_url && (
                <button onClick={() => handlePlay(selectedProject)} className="absolute bottom-4 left-4 flex items-center gap-2 px-4 py-2.5 bg-purple hover:bg-purple/90 text-white rounded-full shadow-lg transition-colors">
                  {playingId === selectedProject.id ? (
                    <><Pause className="w-5 h-5" /><span className="text-sm font-medium">一時停止</span></>
                  ) : (
                    <><Play className="w-5 h-5" /><span className="text-sm font-medium">再生</span></>
                  )}
                </button>
              )}
            </div>

            {/* Info */}
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{selectedProject.title || '無題'}</h2>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {getStatusBadge(selectedProject.status)}
                    <span className="text-xs text-gray-400">{models[selectedProject.model]?.name || selectedProject.model}</span>
                    {selectedProject.has_vocals && (
                      <span className="inline-flex items-center gap-1 text-xs text-purple bg-purple/5 px-2 py-0.5 rounded-full">
                        <Mic className="w-3 h-3" />
                        {selectedProject.vocal_gender === 'male' ? '男性' : '女性'}ボーカル
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Prompt */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs font-medium text-gray-500 mb-1">プロンプト</div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedProject.prompt}</p>
              </div>

              {/* Lyrics */}
              {selectedProject.has_vocals && selectedProject.lyrics_text && (
                <div className="mt-3 p-3 bg-purple/5 rounded-lg">
                  <div className="text-xs font-medium text-purple mb-1">歌詞</div>
                  <p className="text-sm text-purple whitespace-pre-wrap">{selectedProject.lyrics_text}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="mt-4 grid grid-cols-1 gap-3 text-center sm:grid-cols-3">
                <div className="p-2 bg-gray-50 rounded-lg">
                  <FileAudio className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                  <div className="text-xs text-gray-500">サイズ</div>
                  <div className="text-sm font-medium">{formatFileSize(selectedProject.file_size)}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <Clock className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                  <div className="text-xs text-gray-500">作成日</div>
                  <div className="text-sm font-medium">{formatDate(selectedProject.created_at)}</div>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <Music className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                  <div className="text-xs text-gray-500">モデル</div>
                  <div className="text-sm font-medium">{models[selectedProject.model]?.name || 'Lyria'}</div>
                </div>
              </div>

              {selectedProject.error_message && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{selectedProject.error_message}</div>
              )}

              {/* Actions */}
              <div className="mt-6 flex flex-wrap gap-3">
                {selectedProject.audio_url && (
                  <>
                    <button onClick={() => handlePlay(selectedProject)} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-purple text-white rounded-tasky-sm hover:bg-purple/90 transition-colors text-sm font-medium">
                      {playingId === selectedProject.id ? (<><Pause className="w-4 h-4" /> 停止</>) : (<><Play className="w-4 h-4" /> 再生</>)}
                    </button>
                    <button onClick={() => handleDownload(selectedProject)} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-warm-border text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={toggleMute} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-warm-border text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                      {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                  </>
                )}
                <button
                  onClick={() => { handleDelete(selectedProject.id); setSelectedProject(null); }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ResponsiveAppShell>
  );
};

export default MusicCreationPage;
